import React, {useCallback} from 'react';
import {Plugins, withSystem, ExpressionPath} from '@packages/systems/wfdl';
import {
  ExpressionEditor,
  updateExpressionAtAction,
  updateExpressionsAction,
  extendExpressionEditorActionAtPath,
} from '@packages/systems/ExpressionEditor-utils';
import {
  EBoolean,
  ENumber,
  EText,
  TNumber,
  TRecord,
  TText,
  TTypeConstructor,
} from '@packages/systems/wfdl-ast';
import {qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {reportBug} from '@packages/utilities/reporter';
import {
  type ContextualExpression,
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import YouTubeUrlTypeRender from './YouTubeVideoUrl';
import type {VideoHelperResponse} from '@packages/systems/core/utils/EmbedlyUtils';
import type {Expression} from '@packages/systems/wfdl-ast';
import {
  paramToPathsMap,
  EMBED_META_ASPECT_RATIO_PATH,
  EMBED_META_TITLE_PATH,
  EMBED_META_URL_PATH,
  EMBED_META_VIDEO_ID_PATH,
  EMBED_PATH,
} from '../constants';
import {createDevLinkRootAlias} from '@packages/utilities/integrated-types';

const {getIn} = ContextualExpressions;

const convertStringToNumberExpr = (str: string): Expression | null => {
  const num = Number(str);
  const isValid = Number.isInteger(num) && num >= 0;
  return isValid ? ENumber(num) : null;
};

const convertStringToBooleanExpr = (str: string): Expression | null => {
  if (str === '1') return EBoolean(true);
  if (str === '0') return EBoolean(false);
  return null;
};

const constantNull = (_: string) => null;

const parseParams = {
  youtube_rel: convertStringToBooleanExpr,
  youtube_controls: convertStringToBooleanExpr,
  youtube_autoplay: convertStringToBooleanExpr,
  youtube_mute: convertStringToBooleanExpr,
  youtube_start: convertStringToNumberExpr,
  youtube_fs: constantNull,
  youtube_modestbranding: constantNull,
} as const;

const fields = {
  embed: TRecord({
    meta: withSystem(
      {private: true},
      TRecord({
        // Paths that ride in updateMetaExpr's EXPRESSIONS_CHANGED batch.
        videoId: withSystem({isLocalizable: true}, TText),
        aspectRatio: withSystem({isLocalizable: true}, TNumber),
        url: withSystem({isLocalizable: true}, TText),
        title: withSystem({isLocalizable: true}, TText),
      })
    ),
    options: withSystem(
      {labelVariant: 'hidden'},
      TTypeConstructor(
        qualify(Plugins.name('Embed'), typeName('YouTubeOptions'))
      )
    ),
  }),
} as const;

const getExpressionFromPath = (
  expr: ContextualExpression,
  path: ExpressionPath
) => {
  const expression = getIn(expr, path);
  if (!expression)
    throw Error(`failed to get expression at path: ${JSON.stringify(path)}`);
  return expression;
};

const collectOptionChanges = (
  params: Array<{
    property: keyof typeof parseParams;
    value?: string | null;
  }>
): Array<{key: ExpressionPath; newExpression: Expression}> => {
  const changes: Array<{key: ExpressionPath; newExpression: Expression}> = [];

  for (const param of params) {
    const {property, value} = param;
    if (value === null || value === undefined) continue;

    const validate: ((arg1?: any) => Expression | null) | null =
      parseParams[property] || null;
    if (validate === null) continue;

    const val = validate(value);
    if (val === null) continue;

    const path = paramToPathsMap[property];
    if (path === null) continue;

    changes.push({key: path, newExpression: val});
  }

  return changes;
};

export const __testOnly = {collectOptionChanges};

const convertYoutubeParamsToArray = (
  // @ts-expect-error - TS7006 - Parameter 'youTubeParams' implicitly has an 'any' type.
  youTubeParams
): Array<{
  property: keyof typeof parseParams;
  value?: string | null;
}> => {
  const optionKeys = Object.keys(youTubeParams);
  if (optionKeys.length === 0) return [];

  // @ts-expect-error - TS2322 - Type '{ property: string; value: any; }[]' is not assignable to type '{ property: "youtube_rel" | "youtube_controls" | "youtube_fs" | "youtube_modestbranding" | "youtube_mute" | "youtube_autoplay" | "youtube_start"; value?: string | null | undefined; }[]'.
  return optionKeys.map((property) => ({
    property,
    value: youTubeParams[property],
  }));
};

const YouTubeTypeRender = ({
  expr,
  dispatch,
}: {
  expr: ContextualExpression;
  dispatch: (arg1?: any) => void;
}) => {
  const dispatchAt = extendExpressionEditorActionAtPath(dispatch);

  const updateMetaExpr = React.useCallback(
    (response: VideoHelperResponse) => {
      const videoId = response.metadata ? response.metadata.videoId || '' : '';
      const aspectRatio = response.metadata ? response.metadata.aspectRatio : 0;
      const url = response.metadata ? response.metadata.url : '';
      const title = response.metadata ? response.metadata.title : '';
      const youTubeParams = response.metadata
        ? response.metadata.youTubeParams
        : null;

      if (videoId === '' || aspectRatio === 0 || url === '') {
        reportBug(
          'YouTubeAliasMalformedFetchedDataError',
          'YouTube video fetched but without videoId, aspectRatio and/or url.',
          {
            response,
            team: 'platform',
            ticket: 'https://webflow.atlassian.net/browse/PLAT-1009',
          }
        );
      }

      const changes = [
        {key: EMBED_META_VIDEO_ID_PATH, newExpression: EText(videoId)},
        {
          key: EMBED_META_ASPECT_RATIO_PATH,
          newExpression: ENumber(aspectRatio),
        },
        {key: EMBED_META_URL_PATH, newExpression: EText(url)},
        {key: EMBED_META_TITLE_PATH, newExpression: EText(title)},
      ].concat(
        youTubeParams
          ? collectOptionChanges(convertYoutubeParamsToArray(youTubeParams))
          : []
      );

      dispatch(updateExpressionsAction(changes));
    },
    [dispatch]
  );
  const updateTitle = React.useCallback(
    (title: string) => {
      dispatch(updateExpressionAtAction(EText(title), EMBED_META_TITLE_PATH));
    },
    [dispatch]
  );

  const urlExpr = getExpressionFromPath(expr, EMBED_META_URL_PATH);
  const url = urlExpr
    ? ContextualExpressions.getText(stripAnn(urlExpr)) || ''
    : '';
  const titleExpr = getIn(expr, EMBED_META_TITLE_PATH);
  const title = titleExpr
    ? ContextualExpressions.getText(stripAnn(titleExpr)) || ''
    : '';
  const embedAtExpr = getExpressionFromPath(expr, EMBED_PATH);
  // eslint-disable-next-line react-hooks/react-compiler -- pre-ratchet baseline (BLOOP-3033)
  const embedDispatch = useCallback(dispatchAt(EMBED_PATH), [dispatchAt]);

  return (
    <>
      <YouTubeUrlTypeRender
        url={url}
        title={title}
        updateMetaExpr={updateMetaExpr}
        updateTitle={updateTitle}
      />
      <ExpressionEditor expression={embedAtExpr} dispatch={embedDispatch} />
    </>
  );
};

const YouTubeVideoType = createDevLinkRootAlias({
  params: ['extraAtomFields'],
  fields,
  render: YouTubeTypeRender,
});

export default YouTubeVideoType;
